import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FilesetResolver, GestureRecognizer } from '@mediapipe/tasks-vision';
import fistSample from './samples/fist.wav'; // Adjust path as needed
import impulseResponse from './samples/impulse.wav'; // Your impulse response file

// Map gesture labels to imported audio sample URLs.
const sampleURLs = {
  Closed_Fist: fistSample,
};

// Define key signatures.
const keySignatures = {
  "C Major": { 
    semitones: [0, 2, 4, 5, 7, 9, 11, 12],
    notes: ["C", "D", "E", "F", "G", "A", "B", "C"]
  },
  "G Major": { 
    semitones: [7, 9, 11, 12, 14, 16, 18, 19],
    notes: ["G", "A", "B", "C", "D", "E", "F#", "G"]
  },
  "D Major": { 
    semitones: [2, 4, 6, 7, 9, 11, 13, 14],
    notes: ["D", "E", "F#", "G", "A", "B", "C#", "D"]
  },
  "A Major": { 
    semitones: [9, 11, 13, 14, 16, 18, 20, 21],
    notes: ["A", "B", "C#", "D", "E", "F#", "G#", "A"]
  },
  "E Major": { 
    semitones: [4, 6, 8, 9, 11, 13, 15, 16],
    notes: ["E", "F#", "G#", "A", "B", "C#", "D#", "E"]
  },
  "B Major": { 
    semitones: [11, 13, 15, 16, 18, 20, 22, 23],
    notes: ["B", "C#", "D#", "E", "F#", "G#", "A#", "B"]
  },
  "F# Major": { 
    semitones: [6, 8, 10, 11, 13, 15, 17, 18],
    notes: ["F#", "G#", "A#", "B", "C#", "D#", "E#", "F#"]
  },
  "F Major": { 
    semitones: [5, 7, 9, 10, 12, 14, 16, 17],
    notes: ["F", "G", "A", "Bb", "C", "D", "E", "F"]
  },
  "Bb Major": { 
    semitones: [10, 12, 14, 15, 17, 19, 21, 22],
    notes: ["Bb", "C", "D", "Eb", "F", "G", "A", "Bb"]
  },
  "Eb Major": { 
    semitones: [3, 5, 7, 8, 10, 12, 14, 15],
    notes: ["Eb", "F", "G", "Ab", "Bb", "C", "D", "Eb"]
  },
  "Ab Major": { 
    semitones: [8, 10, 12, 13, 15, 17, 19, 20],
    notes: ["Ab", "Bb", "C", "Db", "Eb", "F", "G", "Ab"]
  },
  // Minor keys
  "A Minor": { 
    semitones: [0, 2, 3, 5, 7, 8, 10, 12],
    notes: ["A", "B", "C", "D", "E", "F", "G", "A"]
  },
  "E Minor": { 
    semitones: [7, 9, 10, 12, 14, 15, 17, 19],
    notes: ["E", "F#", "G", "A", "B", "C", "D", "E"]
  },
  "B Minor": { 
    semitones: [2, 4, 5, 7, 9, 10, 12, 14],
    notes: ["B", "C#", "D", "E", "F#", "G", "A", "B"]
  },
  "F# Minor": { 
    semitones: [9, 11, 12, 14, 16, 17, 19, 21],
    notes: ["F#", "G#", "A", "B", "C#", "D", "E", "F#"]
  },
  "C# Minor": { 
    semitones: [4, 6, 7, 9, 11, 12, 14, 16],
    notes: ["C#", "D#", "E", "F#", "G#", "A", "B", "C#"]
  },
  "G# Minor": { 
    semitones: [11, 13, 14, 16, 18, 19, 21, 23],
    notes: ["G#", "A#", "B", "C#", "D#", "E", "F#", "G#"]
  },
  "D# Minor": { 
    semitones: [6, 8, 9, 11, 13, 14, 16, 18],
    notes: ["D#", "E", "F#", "G#", "A#", "B", "C#", "D#"]
  },
  "A# Minor": { 
    semitones: [1, 3, 4, 6, 8, 9, 11, 13],
    notes: ["A#", "C", "C#", "D#", "F", "F#", "G#", "A#"]
  }
};

// Default chromatic scale.
const defaultScale = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// Manual mode: Piano visualizer.
const PianoVisualizer = ({ currentNote, selectedKey }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
      {defaultScale.map((note, index) => {
        let bgColor = 'white';
        if (selectedKey !== "None") {
          const allowed = Array.from(new Set(keySignatures[selectedKey].semitones.map(s => s % 12)));
          if (allowed.includes(index)) {
            bgColor = 'lightblue';
          }
        }
        if (currentNote === index) bgColor = 'yellow';
        return (
          <div
            key={index}
            style={{
              width: '40px',
              height: '150px',
              backgroundColor: bgColor,
              border: '1px solid black',
              margin: '1px',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              fontSize: '14px',
              color: 'black'
            }}
          >
            {note}
          </div>
        );
      })}
    </div>
  );
};

// Auto chord mode: Chord grid visualizer overlaying the entire video preview.
const ChordGridVisualizer = ({ chords, currentCell }) => {
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '640px',
      height: '480px',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: 'repeat(3, 1fr)',
      gap: '0px',
      zIndex: 30,
      backgroundColor: 'rgba(255,255,255,0.4)'
    }}>
      {chords.map((chord, index) => (
         <div key={index} style={{
            border: '1px solid black',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: currentCell === index ? 'yellow' : 'transparent',
            fontSize: '16px'
         }}>
            <div>{chord.name}</div>
            <div style={{ fontSize: '12px', color: 'gray' }}>{chord.roman}</div>
         </div>
      ))}
    </div>
  );
};

// Helper: Get chords (with Roman numeral labels) for a given key (9 chords for 3x3 grid).
const getChordsForKey = (keyName) => {
  let chords = [];
  let roman = [];
  if (keyName === "None") keyName = "C Major";
  if (keyName.includes("Major")) {
    const sig = keySignatures[keyName];
    chords = [
      sig.notes[0] + "maj",
      sig.notes[1] + "min",
      sig.notes[2] + "min",
      sig.notes[3] + "maj",
      sig.notes[4] + "maj",
      sig.notes[5] + "min",
      sig.notes[6] + "dim",
      sig.notes[0] + "maj",
      sig.notes[4] + "maj"
    ];
    roman = ["I", "ii", "iii", "IV", "V", "vi", "vii°", "I", "V"];
  } else if (keyName.includes("Minor")) {
    const sig = keySignatures[keyName];
    chords = [
      sig.notes[0] + "min",
      sig.notes[1] + "dim",
      sig.notes[2] + "maj",
      sig.notes[3] + "min",
      sig.notes[4] + "min",
      sig.notes[5] + "maj",
      sig.notes[6] + "maj",
      sig.notes[0] + "min",
      sig.notes[4] + "min"
    ];
    roman = ["i", "ii°", "III", "iv", "v", "VI", "VII", "i", "v"];
  }
  return chords.map((chord, idx) => ({ name: chord, roman: roman[idx] }));
};

const InteractiveMediaPipe = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [gestureRecognizer, setGestureRecognizer] = useState(null);
  const [webcamEnabled, setWebcamEnabled] = useState(false);
  
  // BPM, note length, key, mode, and arpeggiator settings.
  const [bpm, setBpm] = useState(120);
  const [noteLength, setNoteLength] = useState(1);
  const [selectedKey, setSelectedKey] = useState("None");
  const [mode, setMode] = useState("manual"); // "manual", "autoChord", "arpeggiator"
  const [arpeggioOctaves, setArpeggioOctaves] = useState(1);
  const [arpeggioDirection, setArpeggioDirection] = useState("up"); // "up", "down", "upDown"
  
  // Visual feedback state.
  const [currentNote, setCurrentNote] = useState(null);
  const [currentChordCell, setCurrentChordCell] = useState(null);
  
  // Audio-related refs.
  const audioContextRef = useRef(null);
  const samplesRef = useRef({});
  const convolverRef = useRef(null);
  const notePlayingRef = useRef(false);

  const initAudio = useCallback(async () => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const samples = {};
    for (const [gesture, url] of Object.entries(sampleURLs)) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.error(`Failed to fetch sample for ${gesture}: ${response.statusText}`);
          continue;
        }
        const arrayBuffer = await response.arrayBuffer();
        samples[gesture] = await audioContextRef.current.decodeAudioData(arrayBuffer);
      } catch (error) {
        console.error(`Error loading sample for ${gesture}:`, error);
      }
    }
    samplesRef.current = samples;
    
    try {
      const response = await fetch(impulseResponse);
      if (!response.ok) {
        console.error(`Failed to fetch impulse response: ${response.statusText}`);
      } else {
        const arrayBuffer = await response.arrayBuffer();
        const impulseBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        const convolver = audioContextRef.current.createConvolver();
        convolver.buffer = impulseBuffer;
        convolverRef.current = convolver;
      }
    } catch (error) {
      console.error("Error loading impulse response:", error);
    }
  }, []);

  const initGestureRecognizer = useCallback(async () => {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.1/wasm'
      );
      const recognizer = await GestureRecognizer.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-tasks/gesture_recognizer/gesture_recognizer.task'
        },
        numHands: 2,
        runningMode: 'VIDEO'
      });
      setGestureRecognizer(recognizer);
      console.log("Gesture recognizer initialized.");
    } catch (error) {
      console.error('Error initializing gesture recognizer:', error);
    }
  }, []);

  useEffect(() => {
    initGestureRecognizer();
    initAudio();
    return () => {
      if (gestureRecognizer) {
        gestureRecognizer.close();
      }
    };
  }, [initGestureRecognizer, initAudio]);

  useEffect(() => {
    if (!webcamEnabled) return;
    const video = videoRef.current;
    let stream;
    if (!video) return;
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((s) => {
          stream = s;
          video.srcObject = stream;
          return video.play();
        })
        .then(() => console.log("Video is playing."))
        .catch((e) => console.error('Error playing video:', e));
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [webcamEnabled]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleContextLost = (event) => {
      event.preventDefault();
      console.warn("WebGL context lost; reinitializing gesture recognizer.");
      if (gestureRecognizer) {
        gestureRecognizer.close();
      }
      setGestureRecognizer(null);
      setTimeout(() => {
        initGestureRecognizer();
      }, 1000);
    };
    canvas.addEventListener('webglcontextlost', handleContextLost);
    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost);
    };
  }, [gestureRecognizer, initGestureRecognizer]);

  const getHandPosition = (landmarks) => {
    const avg = landmarks.reduce((acc, landmark) => ({
      x: acc.x + landmark.x,
      y: acc.y + landmark.y
    }), { x: 0, y: 0 });
    return { x: avg.x / landmarks.length, y: avg.y / landmarks.length };
  };

  // Manual mode.
  const playNoteManual = (gestureLabel, handPosition) => {
    const audioCtx = audioContextRef.current;
    if (!audioCtx) return;
    const sampleBuffer = samplesRef.current[gestureLabel];
    if (!sampleBuffer) {
      console.warn(`No sample loaded for gesture: ${gestureLabel}`);
      return;
    }
    if (notePlayingRef.current) return;
    let noteIndex = Math.min(11, Math.floor(handPosition.x * 12));
    setCurrentNote(noteIndex);
    setTimeout(() => setCurrentNote(null), 500);
    console.log(`Playing note for ${gestureLabel}, noteIndex: ${noteIndex}`);
    const duration = (60 / bpm) * noteLength;
    const source = audioCtx.createBufferSource();
    source.buffer = sampleBuffer;
    source.playbackRate.value = Math.pow(2, noteIndex / 12);
    const gainNode = audioCtx.createGain();
    gainNode.gain.value = 0.2 + (1 - handPosition.y) * 0.8;
    source.connect(gainNode);
    if (convolverRef.current) {
      gainNode.connect(convolverRef.current);
      convolverRef.current.connect(audioCtx.destination);
    } else {
      gainNode.connect(audioCtx.destination);
    }
    source.start();
    source.stop(audioCtx.currentTime + duration);
    notePlayingRef.current = true;
    setTimeout(() => { notePlayingRef.current = false; }, duration * 1000);
  };

  // Auto chord mode.
  const playChordFromHandPosition = (gestureLabel, handPosition) => {
    if (gestureLabel !== "Closed_Fist") return;
    if (notePlayingRef.current) return;
    const cellX = Math.floor(handPosition.x * 3);
    const cellY = Math.floor(handPosition.y * 3);
    const cellIndex = cellX + cellY * 3;
    setCurrentChordCell(cellIndex);
    setTimeout(() => setCurrentChordCell(null), 500);
    const chords = getChordsForKey(selectedKey);
    const chordObj = chords[cellIndex];
    playChord(chordObj.name);
  };

  // Arpeggiator mode.
  const playArpeggioFromHandPosition = (gestureLabel, handPosition) => {
    if (gestureLabel !== "Closed_Fist") return;
    if (notePlayingRef.current) return;
    const cellX = Math.floor(handPosition.x * 3);
    const cellY = Math.floor(handPosition.y * 3);
    const cellIndex = cellX + cellY * 3;
    setCurrentChordCell(cellIndex);
    setTimeout(() => setCurrentChordCell(null), 500);
    const chords = getChordsForKey(selectedKey);
    const chordObj = chords[cellIndex];
    playArpeggio(chordObj.name, (60 / bpm) * noteLength, arpeggioOctaves, arpeggioDirection);
  };

  // Play chord (triad) for auto chord mode.
  const playChord = (chordLabel) => {
    const audioCtx = audioContextRef.current;
    if (!audioCtx) return;
    const duration = (60 / bpm) * noteLength;
    const noteToSemitone = { C:0, "C#":1, D:2, "D#":3, E:4, F:5, "F#":6, G:7, "G#":8, A:9, "A#":10, B:11 };
    const match = chordLabel.match(/^[A-G]#?/);
    if (!match) return;
    const root = match[0];
    const chordType = chordLabel.replace(root, "");
    let intervals = [];
    if (chordType === "maj") intervals = [0, 4, 7];
    else if (chordType === "min") intervals = [0, 3, 7];
    else if (chordType === "dim") intervals = [0, 3, 6];
    intervals.forEach(interval => {
      const source = audioCtx.createBufferSource();
      source.buffer = samplesRef.current["Closed_Fist"];
      const rate = Math.pow(2, (noteToSemitone[root] + interval) / 12);
      source.playbackRate.value = rate;
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 0.2;
      source.connect(gainNode);
      if (convolverRef.current) {
        gainNode.connect(convolverRef.current);
        convolverRef.current.connect(audioCtx.destination);
      } else {
        gainNode.connect(audioCtx.destination);
      }
      source.start();
      source.stop(audioCtx.currentTime + duration);
    });
    notePlayingRef.current = true;
    setTimeout(() => { notePlayingRef.current = false; }, duration * 1000);
  };

  // Arpeggiator: play chord as arpeggio.
  const playArpeggio = (chordLabel, duration, arpeggioOctaves, arpeggioDirection) => {
    const audioCtx = audioContextRef.current;
    if (!audioCtx) return;
    const noteToSemitone = { C:0, "C#":1, D:2, "D#":3, E:4, F:5, "F#":6, G:7, "G#":8, A:9, "A#":10, B:11 };
    const match = chordLabel.match(/^[A-G]#?/);
    if (!match) return;
    const root = match[0];
    const chordType = chordLabel.replace(root, "");
    let intervals = [];
    if (chordType === "maj") intervals = [0, 4, 7];
    else if (chordType === "min") intervals = [0, 3, 7];
    else if (chordType === "dim") intervals = [0, 3, 6];
    
    let pattern = [];
    if (arpeggioDirection === "up") {
      pattern = [...intervals];
      if (arpeggioOctaves === 2) {
        pattern = pattern.concat(intervals.map(i => i + 12));
      }
    } else if (arpeggioDirection === "down") {
      pattern = [...intervals];
      if (arpeggioOctaves === 2) {
        pattern = pattern.concat(intervals.map(i => i + 12));
      }
      pattern.reverse();
    } else if (arpeggioDirection === "upDown") {
      let upPattern = [...intervals];
      if (arpeggioOctaves === 2) {
        upPattern = upPattern.concat(intervals.map(i => i + 12));
      }
      let downPattern = upPattern.slice(0, -1).reverse();
      pattern = upPattern.concat(downPattern);
    }
    const totalNotes = pattern.length;
    const noteDuration = duration / totalNotes;
    pattern.forEach((interval, i) => {
      const source = audioCtx.createBufferSource();
      source.buffer = samplesRef.current["Closed_Fist"];
      const rate = Math.pow(2, (noteToSemitone[root] + interval) / 12);
      source.playbackRate.value = rate;
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 0.2;
      source.connect(gainNode);
      if (convolverRef.current) {
        gainNode.connect(convolverRef.current);
        convolverRef.current.connect(audioCtx.destination);
      } else {
        gainNode.connect(audioCtx.destination);
      }
      source.start(audioCtx.currentTime + i * noteDuration);
      source.stop(audioCtx.currentTime + (i + 1) * noteDuration);
    });
    notePlayingRef.current = true;
    setTimeout(() => { notePlayingRef.current = false; }, duration * 1000);
  };

  useEffect(() => {
    if (!gestureRecognizer || !webcamEnabled) return;
    let animationFrameId;
    let lastLogTime = 0;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const processFrame = async () => {
      if (video.readyState >= video.HAVE_ENOUGH_DATA) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const timestamp = performance.now();
        try {
          const results = await gestureRecognizer.recognizeForVideo(video, timestamp);
          if (timestamp - lastLogTime > 1000) {
            console.log("Gesture recognition results:", results);
            lastLogTime = timestamp;
          }
          if (results && results.gestures) {
            results.gestures.forEach((gestureArray, index) => {
              if (gestureArray.length === 0) return;
              const gesture = gestureArray[0];
              console.log("Detected gesture:", gesture);
              const { x, y, width, height } = gesture.boundingBox || {};
              if (x !== undefined) {
                ctx.strokeStyle = 'red';
                ctx.lineWidth = 2;
                ctx.strokeRect(
                  x * canvas.width,
                  y * canvas.height,
                  width * canvas.width,
                  height * canvas.height
                );
              }
              if (gesture.categoryName) {
                console.log("Gesture categoryName:", gesture.categoryName);
                const handLandmarks = results.landmarks[index];
                if (handLandmarks && handLandmarks.length) {
                  const handPos = getHandPosition(handLandmarks);
                  if (mode === "manual") {
                    playNoteManual(gesture.categoryName, handPos);
                  } else if (mode === "autoChord") {
                    playChordFromHandPosition(gesture.categoryName, handPos);
                  } else if (mode === "arpeggiator") {
                    playArpeggioFromHandPosition(gesture.categoryName, handPos);
                  }
                }
              }
            });
          }
          if (results && results.landmarks) {
            results.landmarks.forEach((handLandmarks) => {
              handLandmarks.forEach((landmark) => {
                ctx.beginPath();
                ctx.arc(
                  landmark.x * canvas.width,
                  landmark.y * canvas.height,
                  4,
                  0,
                  2 * Math.PI
                );
                ctx.fillStyle = 'blue';
                ctx.fill();
              });
            });
          }
        } catch (error) {
          console.error('Error processing frame:', error);
        }
      }
      animationFrameId = requestAnimationFrame(processFrame);
    };
    processFrame();
    return () => cancelAnimationFrame(animationFrameId);
  }, [gestureRecognizer, webcamEnabled, bpm, noteLength, selectedKey, mode, arpeggioOctaves, arpeggioDirection]);

  // In manual mode, show piano visualizer below video; in autoChord or arpeggiator, overlay grid.
  const visualizerComponent = mode === "manual" ?
      <PianoVisualizer currentNote={currentNote} selectedKey={selectedKey} />
      : <ChordGridVisualizer chords={getChordsForKey(selectedKey)} currentCell={currentChordCell} />;
  
  return (
    <div style={{ textAlign: 'center', position: 'relative' }}>
      {/* Video preview container */}
      <div style={{ display: 'inline-block', position: 'relative', marginTop: '40px' }}>
        { !webcamEnabled ? (
          <button
            onClick={() => {
              console.log("Enable Webcam button clicked.");
              setWebcamEnabled(true);
              if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().then(() => {
                  console.log("AudioContext resumed.");
                });
              }
            }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              padding: '20px 40px',
              fontSize: '1.2rem',
              zIndex: 20
            }}
          >
            Enable Webcam
          </button>
        ) : (
          <button
            onClick={() => {
              console.log("Stop Video button clicked.");
              setWebcamEnabled(false);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              padding: '10px 20px',
              fontSize: '1rem',
              zIndex: 20
            }}
          >
            Stop Video
          </button>
        )}
        <video
          ref={videoRef}
          style={{ width: '640px', height: '480px' }}
          playsInline
          muted
        />
        <canvas
          ref={canvasRef}
          width={640}
          height={480}
          style={{ position: 'absolute', top: 0, left: 0 }}
        />
        {(mode === "autoChord" || mode === "arpeggiator") && visualizerComponent}
      </div>
      
      {mode === "manual" && visualizerComponent}
      
      {/* Controls below video preview */}
      <div style={{ marginTop: '20px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '10px'
        }}>
          <label>
            BPM:
            <input
              type="number"
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
              style={{ width: '60px', marginLeft: '5px' }}
            />
          </label>
          <label>
            Note Length:
            <select
              value={noteLength}
              onChange={(e) => setNoteLength(Number(e.target.value))}
              style={{ marginLeft: '5px' }}
            >
              <option value={0.25}>Semiquaver (16th note)</option>
              <option value={0.5}>Quaver (8th note)</option>
              <option value={1}>Crotchet (Quarter note)</option>
              <option value={2}>Minim (Half note)</option>
              <option value={4}>Semibreve (Whole note)</option>
            </select>
          </label>
          <label>
            Key:
            <select
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              style={{ marginLeft: '5px' }}
            >
              <option value="None">None (Chromatic)</option>
              {Object.keys(keySignatures).map(keyName => (
                <option key={keyName} value={keyName}>{keyName}</option>
              ))}
            </select>
          </label>
          <label>
            Mode:
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              style={{ marginLeft: '5px' }}
            >
              <option value="manual">Manual</option>
              <option value="autoChord">Auto Chord</option>
              <option value="arpeggiator">Arpeggiator</option>
            </select>
          </label>
          { mode === "arpeggiator" && (
            <>
              <label>
                Arpeggio Octaves:
                <label style={{ marginLeft: '5px' }}>
                  <input
                    type="radio"
                    value={1}
                    checked={arpeggioOctaves === 1}
                    onChange={() => setArpeggioOctaves(1)}
                  /> 1
                </label>
                <label style={{ marginLeft: '5px' }}>
                  <input
                    type="radio"
                    value={2}
                    checked={arpeggioOctaves === 2}
                    onChange={() => setArpeggioOctaves(2)}
                  /> 2
                </label>
              </label>
              <label>
                Arpeggio Direction:
                <label style={{ marginLeft: '5px' }}>
                  <input
                    type="radio"
                    value="up"
                    checked={arpeggioDirection === "up"}
                    onChange={() => setArpeggioDirection("up")}
                  /> Up
                </label>
                <label style={{ marginLeft: '5px' }}>
                  <input
                    type="radio"
                    value="down"
                    checked={arpeggioDirection === "down"}
                    onChange={() => setArpeggioDirection("down")}
                  /> Down
                </label>
                <label style={{ marginLeft: '5px' }}>
                  <input
                    type="radio"
                    value="upDown"
                    checked={arpeggioDirection === "upDown"}
                    onChange={() => setArpeggioDirection("upDown")}
                  /> Up & Down
                </label>
              </label>
            </>
          )}
        </div>
        <button
          onClick={() => {
            console.log("Test Closed_Fist Sample button clicked.");
            if (mode === "manual") {
              playNoteManual('Closed_Fist', { x: 0.5, y: 0.5 });
            } else if (mode === "autoChord") {
              playChord("Cmaj", (60 / bpm) * noteLength);
            } else if (mode === "arpeggiator") {
              playArpeggio("Cmaj", (60 / bpm) * noteLength, arpeggioOctaves, arpeggioDirection);
            }
          }}
          style={{
            marginTop: '10px',
            padding: '10px 20px',
            fontSize: '1rem'
          }}
        >
          Test Closed_Fist Sample
        </button>
      </div>
    </div>
  );
};

export default InteractiveMediaPipe;
